import * as React from "react";
import { FvcAnalysisSet, FvcSession, PefSession, PefTest } from "webportalclient";
import { FvcTest, FvcAnalysisSetTest } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { StudyFvcSetting } from "../../../misc/api/apiTypes";
import ResultsGroupHeading from "../../Shared/ResultsGroupHeading";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { useResultsContext } from "../UseResultsContext";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { PefAnalysisSet } from "webportalclient";
import { truncate } from "../../../misc/math-functions";

interface NextGenPftResultParametersProps {
    session: FvcSession;
    analysisSet: FvcAnalysisSet;
    updatedSession?: FvcSession;
    children: React.ReactNode;
}

interface ParameterContextState {
    session: FvcSession;
    analysisSet: FvcAnalysisSet;
}

export const ParameterContext = React.createContext<ParameterContextState | undefined>(undefined);

export const useParameterContext = (): ParameterContextState => {
    const context = React.useContext(ParameterContext);
    if (context === undefined) {
        throw new Error('useParameterContext must be used within a ParameterContext Provider')
    }

    return context;
}


export const NextGenPftResultParameters = ({ session, analysisSet, children }: NextGenPftResultParametersProps) => {
    return (
        <>
            <ParameterContext.Provider value={{ session, analysisSet }}>
                {children}
            </ParameterContext.Provider>
        </>
    );
}

interface NextGenParameterResultProps {
    name: string;
    identifier: number;
    value: (test: FvcTest & PefTest) => number;
    bestValue?: (analysisSet: FvcAnalysisSet & PefAnalysisSet) => number;
    predictedValue?: (session: FvcSession & PefSession) => number;
    reversibilityBaseValue?: number;
    reversibilityAbsoluteChange?: (analysisSet: FvcAnalysisSet) => number;
    reversibilityPercentageChange?: (analysisSet: FvcAnalysisSet) => number;
    parameterAudience: keyof (Pick<StudyFvcSetting, 'SelectedTestParameters' | 'SelectedAnalysisParameters' | 'SelectedRepeatabilityParameters'>);
}

const reversibilityResults = (parameterConfiguration, analysisSet, showBaseValue, showAbsoluteChange, showPercentageChange, baseValue, absoluteChange, percentageChange) => {
    if (baseValue === null) {
        const colSpan = [showBaseValue, showAbsoluteChange, showPercentageChange].filter(x => x === true).length;

        return (
            <>
                {showBaseValue ? <td colSpan={colSpan}>Not over-read</td> : null}
            </>
        );
    }

    return (
        <>
            {showBaseValue ? <td className="reversibilityBaseValue">{baseValue !== undefined ? baseValue?.toFixed(parameterConfiguration.decimalPlaces) : "-"}</td> : null}
            {showAbsoluteChange ? <td className="reversibilityAbsolute">{absoluteChange !== undefined && (absoluteChange(analysisSet) !== undefined || absoluteChange(analysisSet) !== null) ? absoluteChange(analysisSet)?.toFixed(parameterConfiguration.decimalPlaces) : "-"}</td> : null}
            {showPercentageChange ? <td className="reversibilityPercentage">{percentageChange !== undefined && (percentageChange(analysisSet) !== undefined || percentageChange(analysisSet) !== null) ? truncate(percentageChange(analysisSet), parameterConfiguration.decimalPlaces) + "%" : "-"}</td> : null}
        </>
    )
}

export const NextGenParameterResult = ({ name, identifier, value, bestValue, predictedValue, parameterAudience, reversibilityBaseValue, reversibilityAbsoluteChange, reversibilityPercentageChange }: NextGenParameterResultProps) => {
    const { fvcSettings, pefSettings } = useCurrentStudyConfigurationContext();
    const { parameters } = useReferenceContextProvider();
    const { showBestValues, showPredictedValues, showPercentagePredicted, showReversibilityBaseValue, showReversibilityAbsoluteChange, showReversibilityPercentageChange } = useResultsContext();
    const { session, analysisSet } = useParameterContext();

    const displayed = session.sessionType === SessionTypes.Fvc ? fvcSettings[parameterAudience]?.find(ap => ap === identifier)
        : pefSettings && pefSettings[parameterAudience]?.find(ap => ap === identifier);

    if (!displayed)
        return null;

    const parameterConfiguration = parameters.find(p => p.legacyId === identifier);

    const calculatePercentagePredicted = (analysisSet: FvcAnalysisSet | PefAnalysisSet, session: FvcSession | PefSession) => {
        const bestValueResult = bestValue(analysisSet);
        const predictedValueResult = predictedValue(session);

        if (!bestValueResult || !predictedValueResult)
            return "-";

        return `${((bestValueResult / predictedValueResult) * 100).toFixed(parameterConfiguration.decimalPlaces)}%`;
    }

    const renderTestsParametersResults = (tests: (FvcTest | PefTest)[], analysisSetTests: (FvcAnalysisSetTest)[]) => {
        return (tests.map(t => (
            <td key={`ParameterValue${t.id}${identifier}`} data-automationid={`parameter-value-${t.index}`} className={analysisSetTests.find(ast => ast.testId === t.id)?.accepted ? '' : 'text-muted'}>
                 {value(t) !== undefined && value(t) !== null ? value(t).toFixed(parameterConfiguration.decimalPlaces) : "-"}
                
            </td>
        )));
    }

    return (
        <tr>
            <td className="parameterName">{name}</td>
            {renderTestsParametersResults(session.tests, analysisSet.tests)}
            {showBestValues ? <td className="bestValue">{bestValue !== undefined && bestValue(analysisSet) ? bestValue(analysisSet).toFixed(parameterConfiguration.decimalPlaces) : "-"}</td> : null}
            {reversibilityResults(parameterConfiguration, analysisSet, showReversibilityBaseValue, showReversibilityAbsoluteChange, showReversibilityPercentageChange, reversibilityBaseValue, reversibilityAbsoluteChange, reversibilityPercentageChange)}
            {showPredictedValues ? <td className="predictedValueAbsolute">{predictedValue !== undefined && predictedValue(session) ? predictedValue(session).toFixed(parameterConfiguration.decimalPlaces) : "-"}</td> : null}
            {showPercentagePredicted ? <td className="predictedValuePredicted">{predictedValue !== undefined && calculatePercentagePredicted(analysisSet, session)}</td> : null}
        </tr>
    );
}
